import { createGlobalClassifierIdentifiers } from '@calm/evexia-web-common/dist/circle';
import {
    CircleCircle,
    CircleValidateAndAssignSponsorshipRequest,
} from '@calm/evexia-web-sdk';
import { useTracking } from '@rhg/libs/analytics';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    invalidateQueryKey,
    queryClient,
    queryKeys,
    useMutation,
} from '@rhg/libs/react-query';
import { getRhgAppAppendHeader } from '@rhg/utils/browser';
import { getSSOSessionStorageData } from '@rhg/utils/sso';

interface JoinSponsorshipRequest {
    partnerId: string;
    circle: CircleCircle;
    request: CircleValidateAndAssignSponsorshipRequest;
    willRefreshAllCircles?: boolean;
}
export default function useJoinSponsorship() {
    const { getAccessTokenSilently } = useAuth();
    const { setUserProfile, flush } = useTracking();
    const { isInitialSSO, isLinkedSsoUser } = getSSOSessionStorageData();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async ({
            partnerId,
            circle,
            request,
        }: JoinSponsorshipRequest) => {
            const api = createCircleApi(getAccessTokenSilently);
            const response = await api.v1ValidateAndAssignSponsorship(
                {
                    partnerId,
                    circleAuthorizationToken: circle!.authorizationJWT,
                    recipientId: circle!.recipient.id,
                    circleValidateAndAssignSponsorshipRequest: request,
                },
                {
                    headers: {
                        ...getRhgAppAppendHeader({
                            registration_flow:
                                isInitialSSO && !isLinkedSsoUser
                                    ? 'sso'
                                    : 'direct',
                        }),
                    },
                }
            );
            const classifierIdentifiers = createGlobalClassifierIdentifiers(
                response.data
            );
            await setUserProfile({
                PartnerID: partnerId,
                UserSponsoredCountry:
                    classifierIdentifiers.countryCodeClassifier?.toUpperCase(),
                IsDependent: response.data.sponsor?.isDependentGroup,
            });
            await flush();
            return response.data;
        },
        onSuccess: (newCircle, { willRefreshAllCircles = true }) => {
            queryClient.setQueryData(
                [queryKeys.circleById, newCircle.recipient.id],
                newCircle
            );
            if (willRefreshAllCircles) {
                invalidateQueryKey([queryKeys.circles]);
            }
        },
    });
    return {
        joinSponsorship: mutate,
        joinSponsorshipAsync: mutateAsync,
        ...otherProps,
    };
}
